import { Component, Inject, Input, Optional, inject, OnInit } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { firstValueFrom, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { MasterService } from '../../../master/services/master.service';
import { BaseFormComponent } from '../../../shared/components/base/base-form.component';
import { BookingService } from '../../services/booking.service';
import { ConfirmDialogComponent } from '../../../../shared/components/dialogs/confirm-dialog/confirm-dialog.component';
import { TicketFormV2Component } from '../../../income/ticket/components/ticket-form/ticket-form.components';
import { PreinvoiceFormV2Component } from '../../../income/preinvoice/components/preinvoice-form/preinvoice-form.components';
import { PreinvoiceFormFactory } from '../../../income/preinvoice/models/preinvoice-form.factory';
import { TicketFormFactory } from '../../../income/ticket/models/ticket-form.factory';
import {
  LocationStaffService,
  LocationStaffAdminDto,
} from '../../locations/services/location-staff.service';
import { ConceptService } from '../../../../modules/concept/concept/concept.service';
import { ContactService } from '../../../contacts/services/contact.service';
import { formatLocalIso } from '../../utils/date.utils';

@Component({
    selector: 'app-appointment-form',
    templateUrl: './appointment-form.component.html',
    styleUrls: ['./appointment-form.component.css'],
    standalone: false
})
export class AppointmentFormComponent extends BaseFormComponent implements OnInit {
  @Input() mode: 'page' | 'modal' | 'panel' = 'page';
  form: UntypedFormGroup;

  isSaving = false;
  isDeleting = false;

  concepts: any[] = [];
  employees: any[] = [];
  private allLocations: any[] = [];
  showEmployeeMultiLocationHint = false;

  private readonly fb = inject(UntypedFormBuilder);
  private readonly router = inject(Router);
  private readonly masterService = inject(MasterService);
  private readonly locationStaffService = inject(LocationStaffService);
  private readonly bookingService = inject(BookingService);
  private readonly conceptService = inject(ConceptService);
  private readonly contactService = inject(ContactService);
  protected override readonly dialog = inject(MatDialog);
  protected override readonly snackBar = inject(MatSnackBar);
  public readonly data = inject(MAT_DIALOG_DATA, { optional: true });
  public readonly dialogRef = inject(MatDialogRef<AppointmentFormComponent>, { optional: true });

  constructor() {
    super(inject(MatDialog), inject(MatSnackBar));

    if (this.data && this.data.mode) {
      this.mode = this.data.mode;
    }

    this.form = this.fb.group({
      bookingId: [null],
      locationUid: [null, Validators.required],
      contactUid: [null, Validators.required],
      customerEmail: [null, Validators.email],
      customerPhone: [null],
      conceptUid: [null, Validators.required],
      employeeUid: [null, Validators.required],
      date: [null, Validators.required],
      startTime: [null, Validators.required],
      endTime: [null, Validators.required],
      notes: [''],
    });
  }

  ngOnInit(): void {
    if (this.data && this.data.formValue && this.data.formValue.uid) {
      this.masterService.getContacts().subscribe({
        next: (contacts) => {
          this.bookingService.getById(this.data.formValue.uid).subscribe((response) => {
            if (response && response.isSuccess && response.value) {
              const booking = response.value;

              let contactUid = null;
              if (booking.customerName) {
                const matchingContact = contacts.find(
                  (c) =>
                    this.normalizeValue(c.display || '') ===
                    this.normalizeValue(booking.customerName)
                );

                if (matchingContact) {
                  contactUid = { id: matchingContact.id, display: matchingContact.display };
                } else {
                  contactUid = { id: null, display: booking.customerName };
                }
              }

              this.form.patchValue({
                bookingId: booking.bookingId,
                locationUid: booking.merchantLocationId
                  ? {
                    id: booking.merchantLocationId,
                    display: booking.locationName || '',
                  }
                  : null,
                contactUid,
                customerEmail: booking.customerEmail || '',
                customerPhone: booking.customerPhone || '',
                conceptUid: booking.merchantLocationConceptId
                  ? {
                    id: booking.merchantLocationConceptId,
                    display: booking.conceptName || '',
                  }
                  : null,
                employeeUid: booking.merchantStaffId
                  ? {
                    id: booking.merchantStaffId,
                    display: booking.staffName || '',
                  }
                  : null,
                date: booking.startAt ? new Date(booking.startAt) : null,
                startTime: booking.startAt ? this.extractTime(booking.startAt) : null,
                endTime: booking.endAt ? this.extractTime(booking.endAt) : null,
                notes: booking.notes || '',
              });

              if (booking.merchantLocationId) {
                this.masterService.getConceptsByLocation(booking.merchantLocationId).subscribe({
                  next: (concepts) => {
                    this.concepts = concepts || [];
                  },
                  error: (err) => console.error('Error loading concepts:', err),
                });
              }

              if (booking.merchantLocationId && booking.merchantLocationConceptId) {
                this.masterService
                  .getEmployeesByLocationAndConcept(
                    booking.merchantLocationId,
                    booking.merchantLocationConceptId
                  )
                  .subscribe({
                    next: (employees) => {
                      this.employees = employees || [];
                    },
                    error: (err) => console.error('Error loading employees:', err),
                  });
              }
            }
          });
        },
        error: (err) => {
          console.error('Error loading contacts:', err);
          this.bookingService.getById(this.data.formValue.uid).subscribe((response) => {
            if (response && response.isSuccess && response.value) {
              const booking = response.value;

              this.form.patchValue({
                bookingId: booking.bookingId,
                locationUid: booking.merchantLocationId
                  ? {
                    id: booking.merchantLocationId,
                    display: booking.locationName || '',
                  }
                  : null,
                contactUid: booking.customerName
                  ? { id: null, display: booking.customerName }
                  : null,
                customerEmail: booking.customerEmail || '',
                customerPhone: booking.customerPhone || '',
                conceptUid: booking.merchantLocationConceptId
                  ? {
                    id: booking.merchantLocationConceptId,
                    display: booking.conceptName || '',
                  }
                  : null,
                employeeUid: booking.merchantStaffId
                  ? {
                    id: booking.merchantStaffId,
                    display: booking.staffName || '',
                  }
                  : null,
                date: booking.startAt ? new Date(booking.startAt) : null,
                startTime: booking.startAt ? this.extractTime(booking.startAt) : null,
                endTime: booking.endAt ? this.extractTime(booking.endAt) : null,
                notes: booking.notes || '',
              });
            }
          });
        },
      });
    } else if (this.data && this.data.formValue) {
      this.form.patchValue(this.data.formValue);

      if (!this.form.get('contactUid')?.value) {
        this.applyDefaultSimplifiedInvoiceContact();
      }

      const locationUid = this.data.formValue.locationUid && this.data.formValue.locationUid.id;
      if (locationUid) {
        this.masterService.getConceptsByLocation(locationUid).subscribe({
          next: (concepts) => {
            this.concepts = concepts || [];
          },
          error: (err) => console.error('Error loading concepts:', err),
        });
      }

      const conceptUid = this.data.formValue.conceptUid && this.data.formValue.conceptUid.id;
      if (locationUid && conceptUid) {
        this.masterService.getEmployeesByLocationAndConcept(locationUid, conceptUid).subscribe({
          next: (employees) => {
            this.employees = employees || [];
          },
          error: (err) => console.error('Error loading employees:', err),
        });
      } else {
        const selectedEmployee = this.data.formValue.employeeUid;
        if (selectedEmployee && !locationUid) {
          this.tryAutoSelectLocationForEmployee(selectedEmployee);
        }
      }
    }
  }

  private extractTime(isoString: string): string {
    const date = new Date(isoString);
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  }

  private applyDefaultSimplifiedInvoiceContact(): void {
    this.masterService.getContacts().subscribe({
      next: (contacts) => {
        const defaultContact = (contacts || []).find(
          (c) => c.display && c.display.toLowerCase() === 'factura simplificada'
        );
        if (defaultContact) {
          this.form.get('contactUid')?.setValue({
            id: defaultContact.id,
            display: defaultContact.display,
          });
        } else {
          console.warn('No se encontró el contacto "Factura simplificada".');
        }
      },
      error: (err) => console.error('Error cargando contactos por defecto:', err),
    });
  }

  onLocationChange = (selectedLocation: any): void => {
    const locationUid = selectedLocation && selectedLocation.id;
    const employeeControl = this.form.get('employeeUid');
    const currentEmployee = employeeControl ? employeeControl.value : null;
    this.showEmployeeMultiLocationHint = false;

    this.form.patchValue(
      {
        conceptUid: null,
      },
      { emitEvent: false }
    );

    this.concepts = [];
    this.employees = [];

    if (locationUid) {
      this.masterService.getConceptsByLocation(locationUid).subscribe({
        next: (response) => {
          this.concepts = response || [];
        },
        error: (err) => {
          console.error('Error loading concepts:', err);
          this.concepts = [];
        },
      });

      this.masterService.getEmployeesByLocationAndConcept(locationUid).subscribe({
        next: (response) => {
          this.employees = response || [];

          if (currentEmployee && currentEmployee.display) {
            const alreadyPresent = this.employees.some(
              (e) =>
                e.id === currentEmployee.id ||
                this.normalizeValue(e.display) === this.normalizeValue(currentEmployee.display)
            );

            if (!alreadyPresent) {
              this.employees = [currentEmployee, ...this.employees];
            }
          }
        },
        error: (err) => {
          console.error('Error loading employees:', err);
          this.employees = [];
        },
      });
    }
  };

  private tryAutoSelectLocationForEmployee(selectedEmployee: any): void {
    const employeeId = selectedEmployee && selectedEmployee.id ? selectedEmployee.id : null;
    const employeeDisplay =
      selectedEmployee && selectedEmployee.display ? selectedEmployee.display : null;

    if (!employeeId && !employeeDisplay) {
      return;
    }

    this.masterService.getMerchantLocations().subscribe({
      next: (locations) => {
        this.allLocations = locations || [];

        if (!this.allLocations.length) {
          return;
        }

        const locationChecks = this.allLocations.map((location) =>
          this.locationStaffService.searchLocationStaff(location.id, undefined, undefined, 1, 200).pipe(
            map((staffResponse) => {
              const staff =
                staffResponse && staffResponse.isSuccess && staffResponse.value
                  ? staffResponse.value.staff || []
                  : [];

              return {
                location,
                hasEmployee: this.hasEmployeeInLocationStaff(staff, employeeId, employeeDisplay),
              };
            }),
            catchError(() => of({ location, hasEmployee: false }))
          )
        );

        forkJoin(locationChecks).subscribe((results) => {
          const employeeLocations = results.filter((x) => x.hasEmployee).map((x) => x.location);

          if (employeeLocations.length === 1) {
            this.form.patchValue(
              {
                locationUid: employeeLocations[0],
              },
              { emitEvent: false }
            );

            this.onLocationChange(employeeLocations[0]);
            this.showEmployeeMultiLocationHint = false;
          } else if (employeeLocations.length > 1) {
            this.showEmployeeMultiLocationHint = true;
          } else {
            this.showEmployeeMultiLocationHint = false;
          }
        });
      },
      error: (err) => {
        console.error('Error loading locations for employee mapping:', err);
      },
    });
  }

  private normalizeValue(value: string): string {
    return (value || '').trim().toLowerCase();
  }

  onContactChange = (selectedContact: any): void => {
    if (!selectedContact) {
      return;
    }

    const metadata = selectedContact.metadata || {};
    this.autoFillCustomerFields({
      email: metadata.email || metadata.mail || '',
      phone: metadata.phone || metadata.mobile || '',
    });

    const contactUid = selectedContact.id;
    if (!contactUid) {
      return;
    }

    this.contactService.getByUid(contactUid).subscribe({
      next: (response) => {
        if (response && response.isSuccess && response.value) {
          this.autoFillCustomerFields({
            email: response.value.email || '',
            phone: response.value.phone || '',
          });
        }
      },
      error: (err) => console.error('Error al cargar el detalle del contacto:', err),
    });
  };

  private autoFillCustomerFields(source: { email: string; phone: string }): void {
    const patch: any = {};
    if (source.email) {
      patch.customerEmail = source.email;
    }
    if (source.phone) {
      patch.customerPhone = source.phone;
    }

    if (Object.keys(patch).length > 0) {
      this.form.patchValue(patch, { emitEvent: false });
    }
  }

  private hasEmployeeInLocationStaff(
    staff: LocationStaffAdminDto[],
    employeeId: string | null,
    employeeDisplay: string | null
  ): boolean {
    if (!staff || !staff.length) {
      return false;
    }

    const normalizedEmployeeId = employeeId ? this.normalizeValue(employeeId) : null;

    return staff.some((member) => {
      const byExternalId =
        normalizedEmployeeId &&
        member.employeeExternalId &&
        this.normalizeValue(member.employeeExternalId) === normalizedEmployeeId;
      const byMerchantStaffId =
        normalizedEmployeeId &&
        member.merchantStaffId &&
        this.normalizeValue(member.merchantStaffId) === normalizedEmployeeId;
      const byDisplay =
        employeeDisplay &&
        member.displayName &&
        this.normalizeValue(member.displayName) === this.normalizeValue(employeeDisplay);
      return !!byExternalId || !!byMerchantStaffId || !!byDisplay;
    });
  }

  onConceptChange = (selectedConcept: any): void => {
    const conceptUid = selectedConcept && selectedConcept.id;
    const locationControl = this.form.get('locationUid');
    const locationUid = locationControl && locationControl.value && locationControl.value.id;
    const employeeControl = this.form.get('employeeUid');
    const currentEmployee = employeeControl ? employeeControl.value : null;

    // Autocalcular la hora de fin según la duración del servicio. El usuario
    // puede ampliarla/reducirla después: solo se recalcula al cambiar el
    // concepto, no se vuelve a tocar si edita el fin manualmente.
    this.applyConceptDurationToEndTime(selectedConcept);

    this.employees = [];

    if (locationUid && conceptUid) {
      this.masterService.getEmployeesByLocationAndConcept(locationUid, conceptUid).subscribe({
        next: (response) => {
          this.employees = response || [];

          if (currentEmployee && currentEmployee.display) {
            const alreadyPresent = this.employees.some(
              (e) =>
                e.id === currentEmployee.id ||
                this.normalizeValue(e.display) === this.normalizeValue(currentEmployee.display)
            );

            if (!alreadyPresent) {
              this.employees = [currentEmployee, ...this.employees];
            }
          }
        },
        error: (err) => {
          console.error('Error loading employees:', err);
          this.employees = [];
        },
      });
    }
  };

  /**
   * Ajusta la hora de fin a partir de la duración del concepto seleccionado.
   * Si el concepto no aporta duración o no hay hora de inicio, no toca nada
   * (así, al editar una cita existente, se respeta su fin guardado).
   */
  private applyConceptDurationToEndTime(selectedConcept?: any): void {
    const concept = selectedConcept
      || (this.form.get('conceptUid') ? this.form.get('conceptUid').value : null);

    const durationMinutes = this.getConceptDurationMinutes(concept);
    if (!durationMinutes || durationMinutes <= 0) {
      return;
    }

    const startTimeControl = this.form.get('startTime');
    const startTime = startTimeControl ? startTimeControl.value : null;
    if (!startTime) {
      return;
    }

    const endTime = this.addMinutesToTime(startTime, durationMinutes);
    const endTimeControl = this.form.get('endTime');
    if (endTime && endTimeControl) {
      endTimeControl.setValue(endTime, { emitEvent: false });
    }
  }

  /**
   * Lee la duración (en minutos) del metadata del concepto. Acepta tanto
   * `durationMinutes` (JSON camelCase de la API) como `DurationMinutes`.
   */
  private getConceptDurationMinutes(concept: any): number | null {
    const metadata = concept && concept.metadata ? concept.metadata : null;
    if (!metadata) {
      return null;
    }

    const raw = metadata.durationMinutes !== undefined && metadata.durationMinutes !== null
      ? metadata.durationMinutes
      : metadata.DurationMinutes;

    const value = Number(raw);
    return isNaN(value) ? null : value;
  }

  /**
   * Suma minutos a una hora "HH:mm" y devuelve "HH:mm". Limita al mismo día
   * (no cruza medianoche) para no generar un fin anterior al inicio.
   */
  private addMinutesToTime(time: string, minutesToAdd: number): string | null {
    const parts = (time || '').split(':');
    if (parts.length < 2) {
      return null;
    }

    const hours = Number(parts[0]);
    const minutes = Number(parts[1]);
    if (isNaN(hours) || isNaN(minutes)) {
      return null;
    }

    const total = hours * 60 + minutes + minutesToAdd;
    const clamped = Math.max(0, Math.min(total, 23 * 60 + 59));
    const pad = (n: number) => (n < 10 ? '0' + n : '' + n);
    return `${pad(Math.floor(clamped / 60))}:${pad(clamped % 60)}`;
  }

  getLocations = (): Promise<any> => {
    return new Promise((resolve, reject) => {
      this.masterService.getMerchantLocations().subscribe({
        next: (response) => {
          resolve(response);
        },
        error: (err) => {
          console.error('Error loading locations:', err);
          reject(err);
        },
      });
    });
  };

  getContacts = (): Promise<any> => {
    return new Promise((resolve, reject) => {
      this.masterService.getContacts().subscribe({
        next: (response) => {
          resolve(response);
        },
        error: (err) => {
          console.error('Error loading contacts:', err);
          reject(err);
        },
      });
    });
  };

  getConcepts = (): Promise<any> => {
    return new Promise((resolve, reject) => {
      const locationControl = this.form.get('locationUid');
      const locationUid = locationControl && locationControl.value && locationControl.value.id;

      if (!locationUid) {
        resolve([]);
        return;
      }

      this.masterService.getConceptsByLocation(locationUid).subscribe({
        next: (response) => {
          resolve(response);
        },
        error: (err) => {
          console.error('Error loading concepts:', err);
          reject(err);
        },
      });
    });
  };

  getEmployees = (): Promise<any> => {
    return new Promise((resolve, reject) => {
      const locationControl = this.form.get('locationUid');
      const locationUid = locationControl && locationControl.value && locationControl.value.id;
      const conceptControl = this.form.get('conceptUid');
      const conceptUid = conceptControl && conceptControl.value && conceptControl.value.id;

      if (!locationUid) {
        resolve([]);
        return;
      }

      this.masterService.getEmployeesByLocationAndConcept(locationUid, conceptUid).subscribe({
        next: (response) => {
          resolve(response);
        },
        error: (err) => {
          console.error('Error loading employees:', err);
          reject(err);
        },
      });
    });
  };

  onCancel(): void {
    if (this.mode === 'modal' || this.mode === 'panel') {
      if (this.dialogRef) {
        this.dialogRef.close(null);
      }
    } else if (this.mode === 'page') {
      this.router.navigate(['/appointments']);
    }
  }

  onDelete(): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '350px',
    });

    dialogRef.componentInstance.title = 'Cancelar cita';
    dialogRef.componentInstance.message =
      '¿Estás seguro de que deseas cancelar la cita? El estado cambiará a cancelado.';

    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        this.isDeleting = true;
        const bookingId =
          this.data && this.data.formValue && this.data.formValue.uid
            ? this.data.formValue.uid
            : this.form.value.bookingId;

        if (!bookingId) {
          this.showMessage('No se puede cancelar: ID de cita no encontrado', 'Cerrar', 'error');
          this.isDeleting = false;
          return;
        }

        this.bookingService.cancel(bookingId).subscribe({
          next: (response) => {
            this.isDeleting = false;
            if (response && response.isSuccess) {
              this.showMessage('Cita cancelada correctamente', 'Cerrar', 'warn');
              this.dialog.closeAll();
            } else {
              const errorMsg =
                (response &&
                  response.error &&
                  response.error.messages &&
                  response.error.messages[0]) ||
                'Error al cancelar la cita';
              this.showMessage(`${errorMsg}`, 'Cerrar', 'error');
            }
          },
          error: (err) => {
            this.isDeleting = false;
            console.error('Error al cancelar la cita:', err);
            this.showMessage('Ocurrió un error al cancelar la cita', 'Cerrar', 'error');
          },
        });
      }
    });
  }

  onSave(): void {
    if (this.form.invalid) {
      this.markFormGroupTouched(this.form);
      this.showMessage('Por favor, completa los campos obligatorios 📝', 'Cerrar', 'warn');
      return;
    }

    this.isSaving = true;
    const formValue = this.form.value;
    const startDateTime = this.buildDateTime(formValue.date, formValue.startTime);
    // Fin con duración personalizada. Lo enviamos siempre que el usuario haya
    // indicado una hora de fin posterior al inicio; en caso contrario lo
    // omitimos y el backend calcula el fin con la duración del concepto.
    const endDateTime = this.buildEndDateTime(formValue.date, formValue.startTime, formValue.endTime);

    const bookingId = formValue.bookingId;
    const isUpdate = !!bookingId;

    if (isUpdate) {
      const updateData = {
        bookingId,
        merchantLocationConceptId: formValue.conceptUid && formValue.conceptUid.id,
        merchantStaffId: formValue.employeeUid && formValue.employeeUid.id,
        startAt: startDateTime,
        endAt: endDateTime,
        customerName: (formValue.contactUid && formValue.contactUid.display) || '',
        customerEmail: formValue.customerEmail || null,
        customerPhone: formValue.customerPhone || null,
        notes: formValue.notes || '',
      };

      this.bookingService.update(updateData).subscribe({
        next: (response) => {
          this.isSaving = false;
          if (response && response.isSuccess) {
            this.showMessage('✅ Cita actualizada correctamente', 'Aceptar', 'warn');
            this.dialog.closeAll();
          } else {
            const errorMsg =
              (response &&
                response.error &&
                response.error.messages &&
                response.error.messages[0]) ||
              'Error al actualizar la cita';
            this.showMessage(`❌ ${errorMsg}`, 'Cerrar', 'error');
          }
        },
        error: (err) => {
          this.isSaving = false;
          console.error('Error al actualizar la cita:', err);
          this.showMessage('❌ Ocurrió un error al actualizar la cita', 'Cerrar', 'error');
        },
      });
    } else {
      const createData = {
        merchantLocationId: formValue.locationUid && formValue.locationUid.id,
        merchantLocationConceptId: formValue.conceptUid && formValue.conceptUid.id,
        merchantStaffId: formValue.employeeUid && formValue.employeeUid.id,
        startAt: startDateTime,
        endAt: endDateTime,
        customerName: (formValue.contactUid && formValue.contactUid.display) || '',
        customerEmail: formValue.customerEmail || null,
        customerPhone: formValue.customerPhone || null,
        notes: formValue.notes || '',
      };

      this.bookingService.create(createData).subscribe({
        next: (response) => {
          this.isSaving = false;
          if (response && response.isSuccess) {
            this.showMessage('🎉 Cita creada con éxito', 'Aceptar', 'warn');
            this.dialog.closeAll();
          } else {
            const errorMsg =
              (response &&
                response.error &&
                response.error.messages &&
                response.error.messages[0]) ||
              'Error al crear la cita';
            this.showMessage(`❌ ${errorMsg}`, 'Cerrar', 'error');
          }
        },
        error: (err) => {
          this.isSaving = false;
          console.error('Error al crear la cita:', err);
          this.showMessage('❌ Ocurrió un error al crear la cita', 'Cerrar', 'error');
        },
      });
    }
  }

  private buildDateTime(date: Date, time: string): string {
    if (!date || !time) {
      return formatLocalIso(new Date());
    }

    const d = new Date(date);
    const [hours, minutes] = time.split(':').map(Number);
    d.setHours(hours, minutes, 0, 0);
    return formatLocalIso(d);
  }

  /**
   * Construye el fin con la duración personalizada que indica el usuario.
   * Devuelve null (campo opcional) cuando no hay datos suficientes o la hora
   * de fin no es posterior a la de inicio: en ese caso el backend calcula el
   * fin con la duración del concepto, conservando el comportamiento histórico.
   */
  private buildEndDateTime(date: Date, startTime: string, endTime: string): string | null {
    if (!date || !startTime || !endTime) {
      return null;
    }

    const [startHours, startMinutes] = startTime.split(':').map(Number);
    const [endHours, endMinutes] = endTime.split(':').map(Number);

    // Si el fin no es posterior al inicio (mismo valor o cruce de medianoche)
    // no lo enviamos; dejamos que el backend resuelva la duración por concepto.
    if (endHours * 60 + endMinutes <= startHours * 60 + startMinutes) {
      return null;
    }

    const d = new Date(date);
    d.setHours(endHours, endMinutes, 0, 0);
    return formatLocalIso(d);
  }

  /**
   * Extrae el mensaje real del error. Cubre tanto la respuesta directa
   * (SingleResponseDto con isSuccess=false) como HttpErrorResponse, donde el
   * body parseado vive en err.error y a su vez puede tener un .error anidado
   * con messages[]/detail (ProblemDetails o SingleResponseDto envuelto).
   */
  private extractBookingErrorMessage(source: any, fallback: string): string {
    if (!source) return fallback;

    const candidates: any[] = [
      source.error && source.error.error,
      source.error,
      source
    ];

    for (const c of candidates) {
      if (!c || typeof c !== 'object') continue;
      if (Array.isArray(c.messages) && c.messages.length > 0 && typeof c.messages[0] === 'string') {
        return c.messages[0];
      }
      if (typeof c.detail === 'string' && c.detail.trim()) {
        return c.detail;
      }
    }

    return fallback;
  }

  async onGenerateTicket(): Promise<void> {
    const conceptData = await this.resolveConceptForIncome();
    const ticketForm = TicketFormFactory.buildForm(this.fb);

    if (conceptData) {
      const linesArray = TicketFormFactory.lines(ticketForm);
      const lineGroup = TicketFormFactory.createLine(this.fb);

      lineGroup.patchValue({
        conceptUid: { id: conceptData.id, display: conceptData.display },
        quantity: 1,
        price: conceptData.netPrice || conceptData.price || 0,
        discount: conceptData.discount || 0,
        tax: conceptData.taxes || [],
      });

      linesArray.push(lineGroup);
    }

    this.dialog.open(TicketFormV2Component, {
      width: '100vw',
      height: '100vh',
      hasBackdrop: false,
      panelClass: 'dialog-expand',
      data: {
        mode: 'modal',
        form: ticketForm,
      },
    });
  }

  async onGeneratePreinvoice(): Promise<void> {
    const formValue = this.form.value;
    const conceptData = await this.resolveConceptForIncome();

    const preinvoiceForm = PreinvoiceFormFactory.buildForm(this.fb);

    if (formValue.contactUid) {
      preinvoiceForm.patchValue({ contactUid: formValue.contactUid });
    }

    if (conceptData) {
      const arr = PreinvoiceFormFactory.items(preinvoiceForm);
      const lineGroup = PreinvoiceFormFactory.createLine(this.fb);
      lineGroup.patchValue({
        conceptUid: { id: conceptData.id, display: conceptData.display },
        quantity: 1,
        price: conceptData.netPrice || conceptData.price || 0,
        discount: conceptData.discount || 0,
        tax: conceptData.taxes || [],
      });
      arr.push(lineGroup);
    }

    this.dialog.open(PreinvoiceFormV2Component, {
      width: '100vw',
      height: '100vh',
      hasBackdrop: false,
      panelClass: 'dialog-expand',
      data: {
        mode: 'modal',
        form: preinvoiceForm,
      },
    });
  }

  private async resolveConceptForIncome(): Promise<any | null> {
    const selectedConcept = this.form.get('conceptUid') ? this.form.get('conceptUid')?.value : null;
    if (!selectedConcept) {
      return null;
    }

    const selectedConceptId = selectedConcept.id ? selectedConcept.id : selectedConcept;
    const selectedMetadata = selectedConcept.metadata ? selectedConcept.metadata : null;
    const selectedExternalId =
      selectedMetadata && selectedMetadata.conceptExternalId
        ? selectedMetadata.conceptExternalId
        : null;

    let conceptId = selectedExternalId || selectedConceptId;
    let conceptFromList = selectedConcept;

    if (!selectedMetadata) {
      conceptFromList = this.concepts.filter((c) => c && c.id == selectedConceptId)[0];
      if (conceptFromList) {
        const listMetadata = conceptFromList.metadata ? conceptFromList.metadata : null;
        const listExternalId =
          listMetadata && listMetadata.conceptExternalId ? listMetadata.conceptExternalId : null;
        conceptId = listExternalId || selectedConceptId;
      }
    }

    try {
      const fullConcept = await this.conceptService.loadData(conceptId);

      if (fullConcept) {
        console.log('Loaded concept from API:', fullConcept);

        const allTaxes = await firstValueFrom(this.masterService.getTaxes());
        console.log('All master taxes:', allTaxes);

        const mappedTaxes = (fullConcept.taxes || []).map((conceptTax: any) => {
          const masterTax = allTaxes.find(
            (t: any) =>
              t.id === conceptTax.uid ||
              t.id === String(conceptTax.taxId) ||
              (t.metadata && t.metadata.id === conceptTax.taxId)
          );

          if (masterTax) {
            return masterTax;
          }

          return {
            id: conceptTax.uid || String(conceptTax.taxId),
            display: conceptTax.name,
            metadata: {
              id: conceptTax.taxId || conceptTax.id,
              import: conceptTax.taxImport,
              name: conceptTax.name,
              type: conceptTax.type,
            },
          };
        });

        console.log('Mapped taxes:', mappedTaxes);

        return {
          id: fullConcept.uid,
          display: fullConcept.name,
          price: fullConcept.price,
          netPrice: fullConcept.netPrice,
          discount: fullConcept.discount,
          taxes: mappedTaxes,
          serialNumber: fullConcept.serialNumber,
        };
      }
    } catch (err) {
      console.error('Error loading concept from API:', err);

      if (conceptFromList && conceptFromList.metadata) {
        console.log('Using fallback metadata:', conceptFromList.metadata);
        return {
          id: conceptId,
          display: conceptFromList.display,
          price: conceptFromList.metadata.price,
          netPrice: conceptFromList.metadata.netPrice,
          discount: conceptFromList.metadata.discount,
          taxes: conceptFromList.metadata.taxes || [],
          serialNumber: conceptFromList.metadata.serialNumber,
        };
      }
    }

    return null;
  }

  markFormGroupTouched(formGroup: UntypedFormGroup): void {
    Object.keys(formGroup.controls).forEach(function (key) {
      const control = formGroup.get(key);

      if (control instanceof UntypedFormControl) {
        control.markAsTouched({ onlySelf: true });
      } else if (control instanceof UntypedFormGroup) {
        control.markAsTouched({ onlySelf: true });
        (this as any).markFormGroupTouched(control);
      }
    }, this);
  }

  expand(): void {
    this.dialog.closeAll();
    if (this.mode === 'panel') {
      this.expandToDialog(AppointmentFormComponent);
    } else {
      setTimeout(() => {
        this.dialog.open(AppointmentFormComponent, {
          width: '400px',
          height: '100dvh',
          position: { right: '0', top: '0' },
          hasBackdrop: false,
          panelClass: 'side-dialog-panel',
          data: {
            mode: 'panel',
            formValue: this.form.value,
          },
        });
      }, 200);
    }
  }
}